import React from 'react';
import { useTranslation } from 'react-i18next';
import MetadataEditor from './MetadataEditor';
import PDFDocumentViewerTab from './PDFDocumentViewerTab';
import KBDocumentsTab, { type KBDocumentsTabHandle } from './KBDocumentsTab';
import SearchResultsTab, { type SearchResultsData, type SearchResultDocument } from './SearchResultsTab';
import type { EntitySCV2Config } from '../configs/entitySCV2Config';
import './RightPaneContentSwitcher.css';

export interface DocumentTab {
  id: string;
  title: string;
  url?: string;
  // New fields for PDF Document Viewer with chunks
  kbId?: string;
  chunks?: Array<{
    chunk_id: string;
    text: string;
    chunk_score: number;
    page_number?: number;
    char_start?: number;
    char_end?: number;
  }>;
  isPDFWithChunks?: boolean;
}

interface RightPaneContentSwitcherProps {
  // Entity data
  entityType: string;
  entityId: string;
  entityName: string;
  entity: unknown;
  
  // Tab management
  activeTab: string;
  onTabChange: (tabId: string) => void;
  documentTabs: DocumentTab[];
  onCloseDocumentTab: (tabId: string) => void;
  
  // For MetadataEditor
  fieldConfig: EntitySCV2Config;
  activeField: string | null;
  expandedFields: Record<string, boolean>;
  highlightedFields: Record<string, boolean>;
  onFieldChange: (fieldName: string, value: string) => void;
  onFieldFocus: (fieldName: string) => void;
  onFieldBlur: () => void;
  onToggleExpand: (fieldName: string) => void;
  
  // For sub-project special fields
  searchSessionsData?: unknown[];
  kbsData?: unknown[];
  selectedKbId?: string | null;
  onSelectKb?: (kbId: string) => void;
  onSelectSearch?: (sessionId: string) => void;
  
  // For KB documents
  kbName?: string;
  kbDocumentsTabRef?: React.Ref<KBDocumentsTabHandle>;
  onKbSelectionChange?: (ids: string[]) => void;
  onKbDocumentsDeleted?: (ids: string[]) => void;
  kbDocNumberMap?: Map<string, number>;
  onKbSearchTabCreated?: (transcriptId: string, label: string) => void;
  onKbSearchResultsReady?: (transcriptId: string, documents: import('./KbSearchPanel').KbSearchResultDocument[]) => void;
  onKbSearchError?: (transcriptId: string, message: string) => void;
  
  // For search results tabs
  searchResultsTabs?: SearchResultsData[];
  searchTabSelections?: Map<string, Set<string>>;
  onSearchSelectionChange?: (tabId: string, documentIds: string[]) => void;
  onSaveSearchResults?: (tabId: string, selectedDocumentIds: string[]) => Promise<void>;
  onSaveSearch?: (tabId: string) => Promise<void>;
  onSearchDocumentClick?: (documentId: string, document: SearchResultDocument) => void;

  // For context KB badges in MetadataEditor
  onOpenContextKbTab?: (kbId: string, tabName: string) => void;
  onCreateContextKb?: (context: string, defaultName: string, tabName: string) => void;
  canCreateContextKb?: boolean;
  contextKbsData?: Record<string, { name: string; document_count: number }>;
  customFieldRenderers?: Record<string, React.ReactNode>;
  onOpenWorkspace?: (contextField: string) => void;
  onFufAccept?: (fieldName: string, value: string) => void;
}

const RightPaneContentSwitcher: React.FC<RightPaneContentSwitcherProps> = ({
  entityType,
  entityId,
  entityName,
  entity,
  activeTab,
  onTabChange,
  documentTabs,
  onCloseDocumentTab,
  fieldConfig,
  activeField,
  expandedFields,
  highlightedFields,
  onFieldChange,
  onFieldFocus,
  onFieldBlur,
  onToggleExpand,
  searchSessionsData,
  kbsData,
  selectedKbId,
  onSelectKb,
  onSelectSearch,
  kbName,
  kbDocumentsTabRef,
  onKbSelectionChange,
  onKbDocumentsDeleted,
  kbDocNumberMap,
  onKbSearchTabCreated,
  onKbSearchResultsReady,
  onKbSearchError,
  searchResultsTabs = [],
  searchTabSelections,
  onSearchSelectionChange,
  onSaveSearchResults,
  onSaveSearch,
  onSearchDocumentClick,
  onOpenContextKbTab,
  onCreateContextKb,
  canCreateContextKb,
  contextKbsData,
  customFieldRenderers,
  onOpenWorkspace,
  onFufAccept,
}) => {
  const { t } = useTranslation();

  // Check if activeTab is a search results tab
  const activeSearchResultsTab = searchResultsTabs.find(tab => tab.tabId === activeTab);

  return (
    <div className="right-pane-content">
      {/* Tab Bar */}
      <div className="rpc-tabs-bar">
        {/* Metadata Tab (always present) */}
        <button
          className={`rpc-tab ${activeTab === 'metadata' ? 'active' : ''}`}
          onClick={() => onTabChange('metadata')}
        >
          📊 {t('scv2.metadataTab', 'Metadata')}
        </button>

        {/* Documents Tab (KB only) */}
        {entityType === 'kb' && (
          <button
            className={`rpc-tab ${activeTab === 'documents' ? 'active' : ''}`}
            onClick={() => onTabChange('documents')}
          >
            📄 {t('scv2.documentsTab', 'Documents')}
          </button>
        )}

        {/* Search Results Tabs (Docsi) */}
        {searchResultsTabs.map(tab => (
          <button
            key={tab.tabId}
            className={`rpc-tab ${activeTab === tab.tabId ? 'active' : ''} ${!tab.isSaved ? 'unsaved' : ''}`}
            onClick={() => onTabChange(tab.tabId)}
          >
            {tab.isSaved ? '📁' : '📋'} {tab.tabName}
            <span
              className="tab-close"
              onClick={(e) => {
                e.stopPropagation();
                onCloseDocumentTab(tab.tabId);
              }}
            >
              ×
            </span>
          </button>
        ))}

        {/* Dynamic Document Tabs */}
        {documentTabs.map(doc => (
          <button
            key={doc.id}
            className={`rpc-tab ${activeTab === doc.id ? 'active' : ''}`}
            onClick={() => onTabChange(doc.id)}
          >
            📄 {doc.title}
            <span
              className="tab-close"
              onClick={(e) => {
                e.stopPropagation();
                onCloseDocumentTab(doc.id);
              }}
            >
              ×
            </span>
          </button>
        ))}
      </div>

      {/* Content Area */}
      <div className="rpc-content-area">
        {activeTab === 'metadata' && (
          <MetadataEditor
            entity={entity}
            fieldConfig={fieldConfig}
            activeField={activeField}
            expandedFields={expandedFields}
            highlightedFields={highlightedFields}
            onFieldChange={onFieldChange}
            onFieldFocus={onFieldFocus}
            onFieldBlur={onFieldBlur}
            onToggleExpand={onToggleExpand}
            searchSessionsData={searchSessionsData}
            kbsData={kbsData}
            selectedKbId={selectedKbId}
            onSelectKb={onSelectKb}
            onSelectSearch={onSelectSearch}
            pendingSearchTabs={searchResultsTabs}
            onOpenContextKbTab={onOpenContextKbTab}
            onCreateContextKb={onCreateContextKb}
            canCreateContextKb={canCreateContextKb}
            contextKbsData={contextKbsData}
            customFieldRenderers={customFieldRenderers}
            onOpenWorkspace={onOpenWorkspace}
            onFufAccept={onFufAccept}
            entityType={entityType}
            entityId={entityId}
          />
        )}

        {activeTab === 'documents' && entityType === 'kb' && (
          <KBDocumentsTab
            ref={kbDocumentsTabRef}
            kbId={entityId}
            kbName={entityName}
            onSelectionChange={onKbSelectionChange}
            onDocumentsDeleted={onKbDocumentsDeleted}
            docNumberMap={kbDocNumberMap}
            onSearchTabCreated={onKbSearchTabCreated}
            onSearchResultsReady={onKbSearchResultsReady}
            onSearchError={onKbSearchError}
            onDocumentSelect={(documentId: string, derivativeType: string) => {
              console.log(`Document ${documentId} opened with derivative ${derivativeType}`);
            }}
          />
        )}

        {/* Search Results Tab */}
        {activeSearchResultsTab && onSaveSearchResults && (
          (() => {
            const currentSelections = searchTabSelections?.get(activeTab) || new Set();
            console.log('[RightPane] 🔍 Rendering SearchResultsTab:', {
              activeTab,
              tabData: activeSearchResultsTab.tabId,
              currentSelections: Array.from(currentSelections),
              selectionCount: currentSelections.size
            });
            return (
              <SearchResultsTab
                data={activeSearchResultsTab}
                selectedDocuments={currentSelections}
                onSelectionChange={(documentIds) => {
                  console.log('[RightPane] 📤 SearchResultsTab onSelectionChange called:', {
                    activeTab,
                    documentIds,
                    callbackExists: !!onSearchSelectionChange
                  });
                  onSearchSelectionChange?.(activeTab, documentIds);
                }}
                onSave={onSaveSearchResults}
                onSaveSearch={onSaveSearch}
                onDocumentClick={onSearchDocumentClick}
                selectedKbId={selectedKbId}
                selectedKbName={kbName}
                destinationKbId={activeSearchResultsTab.destinationKbId}
                destinationKbName={activeSearchResultsTab.destinationKbName}
                subProjectId={entityType === 'sub-project' ? entityId : undefined}
              />
            );
          })()
        )}

        {/* Document Viewer Tab */}
        {activeTab !== 'metadata' && activeTab !== 'documents' && !activeSearchResultsTab && (
          (() => {
            const doc = documentTabs.find(d => d.id === activeTab);
            if (!doc) {
              return (
                <div className="rpc-no-content">
                  <p>Document not found</p>
                </div>
              );
            }

            // Use PDFDocumentViewerTab for documents with kbId (works with or without chunks)
            if (doc.kbId) {
              console.log('[RightPane] Rendering PDFDocumentViewerTab for doc:', doc.id);
              console.log('[RightPane] Doc object:', doc);
              console.log('[RightPane] Doc chunks to pass:', doc.chunks);
              return (
                <PDFDocumentViewerTab
                  documentId={doc.id}
                  documentTitle={doc.title}
                  kbId={doc.kbId}
                  chunks={doc.chunks}
                  onClose={() => onCloseDocumentTab(doc.id)}
                />
              );
            }

            // No kbId - show error (fallback removed, all docs should have kbId)
            return (
              <div className="rpc-no-content">
                <p>Cannot display document without Knowledge Base ID</p>
                <p className="rpc-doc-title">{doc.title}</p>
              </div>
            );
          })()
        )}
      </div>
    </div>
  );
};

export default RightPaneContentSwitcher;
